import { Checkbox } from '../Checkbox';
import styles from './MapSetting.module.scss';
import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { changePredictedRouteVisibility, setRouteForSelectedVessel } from 'app/utils/selectedTargetsSlice';
import { useAppDispatch, useAppSelector } from 'app/store/store';
import { mapConfigStateT } from 'app/hooks';
import { SettingState } from '../MapControlPanel/MapControlPanel';
import { FloatingComponent } from '../FloatingComponent';
import { Input } from 'app/components';

interface MapSettingsI {
  isHistoryMode?: boolean;
  showRoutePredicted?: SettingState;
  showRoutes: SettingState;
  showPorts: SettingState;
  showTerminals: SettingState;
  showSeaMarks: SettingState;
  showLegend: SettingState;
  showVesselsNames: SettingState;
  showVessels: SettingState;
  showIndicator: SettingState;
  autoRefresh: SettingState;
  isAutorefrshAllowed: boolean;
  mapConfigState: [mapConfigStateT, React.Dispatch<React.SetStateAction<mapConfigStateT>>];
  showAnchorageState: SettingState;
  showAtons: SettingState;
}

export const MapSettings = ({
  isHistoryMode,
  showRoutePredicted: showRoutePredictedState,
  showRoutes: showRoutesState,
  showPorts: showPortsState,
  showTerminals: showTerminalsState,
  showSeaMarks: showSeaMarksState,
  showLegend: showLegendState,
  showVesselsNames: showVesselsNamesState,
  showVessels: showVesselsState,
  showIndicator: showIndicatorState,
  autoRefresh: autoRefreshState,
  isAutorefrshAllowed,
  mapConfigState,
  showAnchorageState,
  showAtons: showAtonsState,
}: MapSettingsI) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const uiConfig = useAppSelector((state) => state.userSlice.uiConfig);
  const [showSeaMarks, setShowSeaMarks] = showSeaMarksState;
  const [showLegend, setShowLegend] = showLegendState;
  const [showVesselsNames, setShowVesselsNames] = showVesselsNamesState;
  const [showPorts, setShowPorts] = showPortsState;
  const [showTerminals, setShowTerminal] = showTerminalsState;

  const [showVessels, setShowVessels] = showVesselsState;
  const [showRoutes, setShowRoutes] = showRoutesState;
  const [showRoutePredicted, setShowRoutePredicted] = showRoutePredictedState || [];
  const [showAnchorage, setShowAnchorage] = showAnchorageState;
  const [showAtons, setShowAtons] = showAtonsState;

  const [showIndicator, setShowIndicator] = showIndicatorState;
  const [autoRefresh, setAutoRefresh] = autoRefreshState;
  const [selectedMapType, setSelectedMapType] = mapConfigState;

  const handleMapTypeChange: React.ChangeEventHandler<HTMLInputElement> = (event: ChangeEvent<HTMLInputElement>) => {
    localStorage.setItem('selectedMapType', JSON.stringify({ mapname: event.target.value }));
    setSelectedMapType({ mapname: event.target.value });
  };

  const handleCheckboxChange = (item: string, itemState: boolean) => {
    localStorage.setItem(item.toString(), JSON.stringify(!itemState));
    if (item === 'showSeaMarks') {
      setShowSeaMarks(!itemState);
    }
    if (item === 'showLegend') {
      setShowLegend(!itemState);
    }
    if (item === 'showVesselsNames') {
      setShowVesselsNames(!itemState);
    }
    if (item === 'showPorts') {
      setShowPorts(!itemState);
    }
    if (item === 'showTerminals') {
      setShowTerminal(!itemState);
    }
    if (item === 'showVessels') {
      setShowVessels(!itemState);
    }
    if (item === 'showIndicator') {
      setShowIndicator(!itemState);
    }
    if (item === 'autoRefresh') {
      setAutoRefresh(!itemState);
    }
    if (item === 'showAnchorage') {
      setShowAnchorage(!itemState);
    }
    if (item === 'showRoutes') {
      setShowRoutes(!itemState);
      dispatch(setRouteForSelectedVessel({ isShown: !itemState }));
    }
    if (item === 'showAtons') {
      setShowAtons(!itemState);
    }
  };

  return (
    <FloatingComponent>
      <div className={styles.checkbox_group}>
        <Checkbox
          label={t('SettingLabelShowVessels')}
          className={styles.checkbox}
          checked={showVessels}
          onChange={() => handleCheckboxChange('showVessels', showVessels)}
        />

        <Checkbox
          label={t('SettingLabelShowPorts')}
          className={styles.checkbox}
          checked={showPorts}
          onChange={() => handleCheckboxChange('showPorts', showPorts)}
        />
        <Checkbox
          label={t('SettingLabelShowTerminals')}
          className={styles.checkbox}
          checked={showTerminals}
          onChange={() => handleCheckboxChange('showTerminals', showTerminals)}
        />
        {!window._env_.ATONSBYFILTER && (
          <Checkbox
            label={t('Atons')}
            className={styles.checkbox}
            onChange={() => handleCheckboxChange('showAtons', showAtons)}
            checked={showAtons}
          />
        )}
        <Checkbox
          label={t('NauticalChart')}
          className={styles.checkbox}
          checked={showSeaMarks}
          onChange={() => handleCheckboxChange('showSeaMarks', showSeaMarks)}
        />
        <Checkbox
          label={t('Anchorage')}
          className={styles.checkbox}
          checked={showAnchorage}
          onChange={() => handleCheckboxChange('showAnchorage', showAnchorage)}
        />
      </div>
      <div className={styles.checkbox_group}>
        <Checkbox
          label={t('SettingLabelShowLegend')}
          className={styles.checkbox}
          onChange={() => handleCheckboxChange('showLegend', showLegend)}
          checked={showLegend}
        />
        <Checkbox
          label={t('VesselsNames')}
          className={styles.checkbox}
          onChange={() => handleCheckboxChange('showVesselsNames', showVesselsNames)}
          checked={showVesselsNames}
        />
        <Checkbox
          label={t('SettingLabelShowGroupIndicator')}
          checked={showIndicator}
          className={styles.checkbox}
          disabled={isHistoryMode}
          onChange={() => handleCheckboxChange('showIndicator', showIndicator)}
        />
        <Checkbox
          label={t('SettingLabelShowRoutes')}
          className={styles.checkbox}
          onChange={() => handleCheckboxChange('showRoutes', showRoutes)}
          disabled={isHistoryMode}
          checked={showRoutes}
        />

        {showRoutePredictedState && uiConfig?.routeBuilderAvailable && (
          <Checkbox
            label={t('SettingLabelShowRoutePredicted')}
            className={styles.checkbox}
            onChange={() => {
              dispatch(changePredictedRouteVisibility(!showRoutePredicted));

              setShowRoutePredicted && setShowRoutePredicted((prev) => !prev);
            }}
            disabled={isHistoryMode}
            checked={Boolean(showRoutePredicted)}
          />
        )}
        <Checkbox
          label={t('SettingLabelAutoRefresh')}
          className={styles.checkbox}
          onChange={() => handleCheckboxChange('autoRefresh', autoRefresh)}
          disabled={!isAutorefrshAllowed || isHistoryMode}
          checked={autoRefresh && !isHistoryMode}
        />
      </div>
      <div className={styles.radio_group}>
        <Input
          label={t('VectorMap')}
          name="mapType"
          type="radio"
          value="DARK_PURPLE_MAP"
          onChange={handleMapTypeChange}
          checked={selectedMapType.mapname === 'DARK_PURPLE_MAP'}
        />
        <Input
          label={t('SatelliteView')}
          name="mapType"
          type="radio"
          value="SATELLITE_MAP"
          onChange={handleMapTypeChange}
          checked={selectedMapType.mapname === 'SATELLITE_MAP'}
        />
      </div>
    </FloatingComponent>
  );
};
